/**
 * Sets API hooks - One-time load via Jotai atom
 *
 * Architecture:
 * - useSetsSync() loads all sets from Firestore once and populates setsStateAtom
 * - Returns isLoading=true until data is fetched, used by app initialization
 * - All consumers read from the shared Jotai atom via useSetsQuery()
 * - No real-time subscription needed since set data is static (updated weekly)
 */
import { useEffect, useRef, useState } from 'react';

import type { DocumentData } from 'firebase/firestore';
import { collection, getDocs, orderBy, query } from 'firebase/firestore';
import { useAtomValue, useSetAtom } from 'jotai';

import { firestore } from '@/services/firebase';
import type { ScryfallSet } from '@/types/card';
import { setsStateAtom } from '../state/setsAtom';

// Re-export state atom for consumers who need direct access
export { setsStateAtom } from '../state/setsAtom';

// ============================================================================
// Sync Hook
// ============================================================================

/**
 * Loads all sets from Firestore once and populates the setsStateAtom.
 * Returns true while loading (until data is fetched).
 *
 * Unlike other sync hooks, this doesn't use real-time subscription because
 * set data is static and only updated weekly by backend functions.
 *
 * @example
 * ```tsx
 * const isLoadingSets = useSetsSync();
 * ```
 */
export function useSetsSync(): boolean {
  const setState = useSetAtom(setsStateAtom);
  const [isLoading, setIsLoading] = useState(true);
  const hasLoadedRef = useRef(false);

  useEffect(() => {
    // Only load once
    if (hasLoadedRef.current) return;
    hasLoadedRef.current = true;

    async function loadSets() {
      try {
        const setsQuery = query(collection(firestore, 'sets'), orderBy('released_at', 'desc'));

        const snapshot = await getDocs(setsQuery);

        const sets: ScryfallSet[] = snapshot.docs.map((doc: DocumentData) => ({
          id: doc.id,
          ...doc.data(),
        })) as ScryfallSet[];

        setState({
          data: sets,
          isLoading: false,
          error: null,
        });
        setIsLoading(false);
      } catch (error) {
        console.error('Error loading sets:', error);
        setState({
          data: undefined,
          isLoading: false,
          error: error instanceof Error ? error : new Error('Failed to load sets'),
        });
        setIsLoading(false);
      }
    }

    void loadSets();
  }, [setState]);

  return isLoading;
}

// ============================================================================
// Public Hooks
// ============================================================================

/**
 * Read-only hook for all MTG sets.
 * Returns the current sets state from the shared Jotai atom.
 */
export function useSetsQuery() {
  const state = useAtomValue(setsStateAtom);
  return {
    data: state.data,
    isLoading: state.isLoading,
    error: state.error,
  };
}

/**
 * Get a set by its code (case-insensitive).
 */
export function useSetByCode(code: string | null): ScryfallSet | null {
  const { data: sets } = useSetsQuery();
  if (!sets || !code) return null;
  const lowerCode = code.toLowerCase();
  return sets.find((s) => s.code.toLowerCase() === lowerCode) ?? null;
}

/**
 * Get sets grouped by set_type for dropdown grouping.
 * Returns a Map with set_type as key and array of sets as value.
 */
export function useSetsGroupedByType(): Map<string, ScryfallSet[]> {
  const { data: sets } = useSetsQuery();

  if (!sets) return new Map();

  const grouped = new Map<string, ScryfallSet[]>();

  for (const set of sets) {
    const type = set.set_type;
    if (!grouped.has(type)) {
      grouped.set(type, []);
    }
    grouped.get(type)!.push(set);
  }

  return grouped;
}

/**
 * Transform sets into react-select options format.
 * Grouped by set_type with format: "Set Name (CODE) - Year"
 */
export function transformSetsToOptions(sets: ScryfallSet[]): Array<{
  label: string;
  options: Array<{
    value: string; // set code
    label: string;
    set: ScryfallSet;
  }>;
}> {
  // Group sets by type
  const grouped = new Map<string, ScryfallSet[]>();

  for (const set of sets) {
    const type = set.set_type;
    if (!grouped.has(type)) {
      grouped.set(type, []);
    }
    grouped.get(type)!.push(set);
  }

  // Define order for set types (most common first)
  const typeOrder = [
    'expansion',
    'core',
    'masters',
    'draft_innovation',
    'commander',
    'funny',
    'starter',
    'duel_deck',
    'from_the_vault',
    'premium_deck',
    'spellbook',
    'masterpiece',
    'arsenal',
    'box',
    'promo',
    'token',
    'memorabilia',
    'planechase',
    'archenemy',
    'vanguard',
    'treasure_chest',
    'alchemy',
    'minigame',
  ];

  const formatLabel = (type: string): string => {
    // Convert snake_case to Title Case
    return type
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const formatYear = (releasedAt: string | null): string => {
    if (!releasedAt) return '';
    try {
      const year = new Date(releasedAt).getFullYear();
      return isNaN(year) ? '' : String(year);
    } catch {
      return '';
    }
  };

  // Sort types by defined order, unknown types go to end
  const sortedTypes = [...grouped.keys()].sort((a, b) => {
    const aIndex = typeOrder.indexOf(a);
    const bIndex = typeOrder.indexOf(b);
    if (aIndex === -1 && bIndex === -1) return a.localeCompare(b);
    if (aIndex === -1) return 1;
    if (bIndex === -1) return -1;
    return aIndex - bIndex;
  });

  return sortedTypes.map((type) => ({
    label: formatLabel(type),
    options: grouped.get(type)!.map((set) => {
      const year = formatYear(set.released_at);
      return {
        value: set.code,
        label: `${set.name} (${set.code.toUpperCase()})${year ? ` - ${year}` : ''}`,
        set,
      };
    }),
  }));
}
